import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Missing Supabase environment variables. Check your .env.local file.");
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

/** Canonical DB types — source: supabase/migrations. See @/types/database for row shapes. */
export type Database = {
  public: {
    Tables: {
      decks: {
        Row: {
          id: string;
          user_id: string;
          title: string;
          description: string | null;
          lang: "en" | "es" | "fr" | "de" | "it" | "nl" | "pl" | "pt-BR" | "tr" | "ko" | "ru" | "uk";
          emoji: string | null;
          level: string | null;
          deck_type: string | null;
          type: string | null;
          share_token: string | null;
          share_access: "read" | "write" | null;
          study_modes: Record<string, boolean> | null;
          created_at: string;
          updated_at: string | null;
        };
        Insert: Omit<Database["public"]["Tables"]["decks"]["Row"], "created_at" | "updated_at">;
        Update: Partial<Database["public"]["Tables"]["decks"]["Insert"]>;
      };
      cards: {
        Row: {
          id: string;
          deck_id: string;
          front: string;
          back: string;
          hint: string | null;
          card_type: "grammar" | "vocabulary" | null;
          level: string | null;
          position: number;
          front_language: string | null;
          back_language: string | null;
          definition: string | null;
          example: string | null;
          image_url: string | null;
          ai_explanation: string | null;
          test_data: Record<string, { question: string; options: string[]; correctIndex: number; hint?: string }> | null;
          front_audio_url: string | null;
          back_audio_url: string | null;
          front_audio_locale: string | null;
          back_audio_locale: string | null;
          created_at: string;
        };
        Insert: Omit<Database["public"]["Tables"]["cards"]["Row"], "created_at">;
        Update: Partial<Database["public"]["Tables"]["cards"]["Insert"]>;
      };
      progress: {
        Row: {
          id: string;
          user_id: string;
          card_id: string;
          correct_count: number;
          incorrect_count: number;
          last_reviewed_at: string;
        };
        Insert: Omit<Database["public"]["Tables"]["progress"]["Row"], "last_reviewed_at">;
        Update: Partial<Database["public"]["Tables"]["progress"]["Insert"]>;
      };
      deck_mode_progress: {
        Row: {
          id: string;
          user_id: string;
          deck_id: string;
          mode: string;
          progress_pct: number;
          updated_at: string;
        };
        Insert: Omit<Database["public"]["Tables"]["deck_mode_progress"]["Row"], "id">;
        Update: Partial<Database["public"]["Tables"]["deck_mode_progress"]["Insert"]>;
      };
      deck_game_progress: {
        Row: {
          id: string;
          user_id: string;
          deck_id: string;
          game_id: string;
          progress_pct: number;
          updated_at: string;
        };
        Insert: Omit<Database["public"]["Tables"]["deck_game_progress"]["Row"], "id">;
        Update: Partial<Database["public"]["Tables"]["deck_game_progress"]["Insert"]>;
      };
      feedback: {
        Row: {
          id: string;
          name: string | null;
          rating: number;
          message: string | null;
          status: "pending" | "approved" | "archived";
          is_published: boolean;
          created_at: string;
        };
        Insert: {
          name?: string | null;
          rating: number;
          message?: string | null;
          status?: "pending" | "approved" | "archived";
          is_published?: boolean;
        };
        Update: Partial<Database["public"]["Tables"]["feedback"]["Insert"]>;
      };
      user_card_progress: {
        Row: {
          id: string;
          user_id: string;
          card_id: string;
          deck_id: string;
          times_seen: number;
          times_correct: number;
          times_incorrect: number;
          current_streak: number;
          best_streak: number;
          avg_response_ms: number | null;
          last_response_ms: number | null;
          hint_used_count: number;
          explain_used_count: number;
          times_flipped: number;
          learn_success_count: number;
          test_success_count: number;
          game_success_count: number;
          learn_fail_count: number;
          test_fail_count: number;
          game_fail_count: number;
          last_seen_at: string | null;
          last_correct_at: string | null;
          last_incorrect_at: string | null;
          first_seen_at: string | null;
          difficulty_score: number | null;
          difficulty_level: "easy" | "medium" | "hard" | "critical" | null;
          memory_strength: number | null;
          forgetting_risk: number | null;
          stability_score: number | null;
          next_review_at: string | null;
          overdue_score: number | null;
          review_priority: number | null;
          recommended_mode: "cards" | "learn" | "test" | "games" | null;
          last_review_bucket: string | null;
          last_smart_review_at: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database["public"]["Tables"]["user_card_progress"]["Row"], "id" | "created_at" | "updated_at"> & { created_at?: string; updated_at?: string };
        Update: Partial<Database["public"]["Tables"]["user_card_progress"]["Insert"]>;
      };
      user_card_events: {
        Row: {
          id: string;
          user_id: string;
          card_id: string;
          deck_id: string;
          mode: string;
          answered_correctly: boolean;
          response_time_ms: number | null;
          hint_used: boolean;
          explain_used: boolean;
          flipped: boolean;
          occurred_at: string;
        };
        Insert: Omit<Database["public"]["Tables"]["user_card_events"]["Row"], "id">;
        Update: Partial<Database["public"]["Tables"]["user_card_events"]["Insert"]>;
      };
    };
  };
}
